"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback } from "react";
interface UseLongPressInteractionOptions {
  longPressDuration?: number | undefined;
  moveThreshold?: number | undefined;
  onInteractionStart?: (() => void) | undefined;
  hasTouchScreen?: boolean | undefined;
  /**
   * Whether to call preventDefault on touch events. Defaults to true to
   * suppress selection/scroll, but can be disabled when downstream logic
   * relies on the synthetic click event (e.g. tap-to-toggle buttons).
   */
  preventDefault?: boolean | undefined;
}

/**
 * Hook for handling long press touch interactions.
 * Detects when a user holds their finger on an element and manages the resulting state.
 * Can be used to trigger various actions like showing menus, selections, or other UI responses.
 *
 * @param options Configuration options for the long press behavior, including hasTouchScreen which must be provided externally
 * @returns Object with interaction state and touch event handlers
 */
export default function useLongPressInteraction(t0) {
  const $ = _c(20);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    longPressDuration: t2,
    moveThreshold: t3,
    onInteractionStart,
    hasTouchScreen: t4,
    preventDefault: t5
  } = options;
  const longPressDuration = t2 === undefined ? 500 : t2;
  const moveThreshold = t3 === undefined ? 10 : t3;
  const hasTouchScreen = t4 === undefined ? false : t4;
  const preventDefault = t5 === undefined ? true : t5;
  const [isActive, setIsActive] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const longPressTimeout = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  let t6;
  if ($[2] !== onInteractionStart) {
    t6 = () => {
      setIsActive(true);
      setLongPressTriggered(true);
      onInteractionStart?.();
    };
    $[2] = onInteractionStart;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const startInteraction = t6;
  let t7;
  if ($[4] !== hasTouchScreen || $[5] !== longPressDuration || $[6] !== preventDefault || $[7] !== startInteraction) {
    t7 = e => {
      if (!hasTouchScreen) {
        return;
      }
      if (preventDefault && e.cancelable) {
        e.preventDefault();
      }
      touchStartX.current = e.touches[0]?.clientX;
      touchStartY.current = e.touches[0]?.clientY;
      longPressTimeout.current = setTimeout(() => {
        startInteraction();
      }, longPressDuration);
    };
    $[4] = hasTouchScreen;
    $[5] = longPressDuration;
    $[6] = preventDefault;
    $[7] = startInteraction;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleTouchStart = t7;
  let t8;
  if ($[9] !== moveThreshold || $[10] !== preventDefault) {
    t8 = e_0 => {
      if (!longPressTimeout.current) {
        return;
      }
      if (preventDefault && e_0.cancelable) {
        e_0.preventDefault();
      }
      const touchX = e_0.touches[0]?.clientX;
      const touchY = e_0.touches[0]?.clientY;
      const deltaX = Math.abs(touchX - touchStartX.current);
      const deltaY = Math.abs(touchY - touchStartY.current);
      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        clearTimeout(longPressTimeout.current);
        longPressTimeout.current = null;
      }
    };
    $[9] = moveThreshold;
    $[10] = preventDefault;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleTouchMove = t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (longPressTimeout.current) {
        clearTimeout(longPressTimeout.current);
        longPressTimeout.current = null;
      }
      setLongPressTriggered(false);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleTouchEnd = t9;
  let t10;
  if ($[13] !== handleTouchMove || $[14] !== handleTouchStart) {
    t10 = {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchEnd
    };
    $[13] = handleTouchMove;
    $[14] = handleTouchStart;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== isActive || $[17] !== longPressTriggered || $[18] !== t10) {
    t11 = {
      isActive,
      setIsActive,
      longPressTriggered,
      touchHandlers: t10
    };
    $[16] = isActive;
    $[17] = longPressTriggered;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
}